// Reflect: Angular selbst macht es sich an solchen stellen einfach und deklariert Reflect als any.
// Siehe https://github.com/angular/angular/blob/2.0.0-beta.9/modules/angular2/src/facade/lang.ts

// eslint-disable-next-line no-var
declare var Reflect: any;

/* Funktioniert, weil der UI-Build-Task Metadaten mit dem Key "portalModuleAnnotations" hinzufügt. */
// todo: metadata: IPortalComponentMetadata
/**
 * Annotation für Portal-Module. In diesen Modulen wird nach
 * Komponenten mit einer {@link PortalComponent}-Annotation gesucht.
 */
export function PortalModule() {
    return function(target) {
        Reflect.defineMetadata('portalModuleAnnotations', true, target);
    };
}

/**
 * Annotation für Portal-Komponenten mit {@link IPortalComponentMetadata} als Parameter.
 * Angular-Komponenten (@Component) werden zusätzlich mit dieser Annotation annotiert,
 * wenn der UI Build Metadaten generieren soll, also Einträge in der bundles.json für
 * den jeweiligen UI-Build (z.B. user, portal, content). Mithilfe dieser Daten können
 * im Portaladministrationsmodus Komponenten auf der Seite platziert werden.
 *
 * Muss in einem {@link PortalModule} deklariert werden.
 *
 * @param metadata Metadaten des Inputs
 */
export function PortalComponent(metadata: IPortalComponentMetadata) {
    return function(target) {
        const annotations = (Reflect.getMetadata('portalAnnotations', target) || [])
            // Das new ist nötig, damit auch zur Runtime der Typ korrekt ist
            .concat(Object.assign(new PortalComponentMetadata(), metadata));
        Reflect.defineMetadata('portalAnnotations', annotations, target);
    };
}

/**
 * Annotation für Komponenten-Inputs mit {@link IPortalComponentInputMetadata} als Parameter.
 * Aus diesen Metadaten wird ein "inputs"-Array in der bundles.json pro Komponente generiert,
 * wenn das UI gebaut wird. Damit kann ein µService neben den Komponenten, die er bereitstellt,
 * auch sagen, welche Inputparameter vorhanden sind, damit man diese im Portaladministrationsmodus
 * entsprechend setzen/binden kann.
 *
 * Muss Teil einer {@link PortalComponent} sein.
 *
 * @param metadata Metadaten des Inputs
 */
export function PortalComponentInput(metadata: IPortalComponentInputMetadata) {
    return function(target, propertyKey: string) {
        addPropertyMetadata(target, propertyKey, Object.assign(new PortalComponentInputMetadata(), metadata));
    };
}

/**
 * Annotation für Komponenten-Outputs mit {@link IPortalComponentOutputMetadata} als Parameter.
 * @param metadata Metadaten des Outputs
 */
export function PortalComponentOutput(metadata: IPortalComponentOutputMetadata) {
    return function(target, propertyKey: string) {
        addPropertyMetadata(target, propertyKey, Object.assign(new PortalComponentOutputMetadata(), metadata));
    };
}

function addPropertyMetadata(target, propertyKey: string, metadata) {
    const propertiesMetadata = Reflect.getMetadata('portalPropMetadata', target.constructor) || {};
    const annotations = (propertiesMetadata[propertyKey] || []).concat(metadata);
    propertiesMetadata[propertyKey] = annotations;

    Reflect.defineMetadata('portalPropMetadata', propertiesMetadata, target.constructor);
}

/**
 * Properties, die an eine {@link PortalComponentInput}-Annotation übergeben werden.
 */
export interface IPortalComponentMetadata {
    /**
     * Der Name der Komponente, wie er dem fachlichen Administrator angezeigt werden soll.
     */
    displayName: string;

    /**
     * Die ausführliche Beschreibung der Komponente, wie sie dem fachlichen Administrator angezeigt werden soll.
     */
    description: string;

}

/**
 * Siehe {@link IPortalComponentMetadata}.
 */
export class PortalComponentMetadata implements IPortalComponentMetadata {
    displayName: string;
    description: string;
}

/**
 * Wie {@link IPortalComponentMetadata}.
 */
export class PortalModuleMetadata implements IPortalComponentMetadata {
    displayName: string;
    description: string;
}

/**
 * Properties, die an eine {@link PortalComponentInput}-Annotation übergeben werden.
 */
export interface IPortalComponentInputMetadata {

    /**
     * Die ausführliche Beschreibung des Input-Parameters, wie sie dem fachlichen Administrator angezeigt werden soll.
     */
    description: string;

    /**
     * Der Typ des Inputparameters. Falls nicht gesetzt, wird standardmäßig 'string' verwendet,
     * also ein normales Eingabefeld.
     *
     * Der Typ dient dazu, im Admin-Bereich passende Formularfelder anzuzeigen, bei denen auch
     * Inputkomponenten aus anderen Microservices (anderen Angularbundles) genutzt werden können,
     * beispielweise die Rollenauswahl aus SPP User.
     *
     * Wir verwenden in SPP u.a. Werte, wie "role" oder "portallink". In diesen Fällen wird nicht
     * ein normales Input-Feld angezeigt, sondern ein AdminFormFieldProvider, der eine "schöne"
     * Auswahl der Seite, der Rolle oder ähnlichem ermöglicht.
     *
     * Damit bspw. der Type "portallink" zu einer nützlichen Inputkomponente aufgelöst werden kann,
     * muss eine Portalkomponente das Interface {@link IAdminFormFieldProvider} implementieren und in
     * der {@link IAdminFormFieldProvider#getFormFieldTemplate}-Methode type überprüfen. Dann
     * kann ein entsprechendes HTML Template für das Formular überreicht werden.
     *
     * Dafür gibt es Beispiele in SPP User und SPP Portal.
     */
    type?: string;

    /**
     * true bedeutet, das der Input-Parameter eine Liste von Werten haben kann, von denen jeder jeweils
     * vom oben angegebene type ist. Die Werte werden grundsätzlich kommasepariert (,) gespeichert.
     */
    multi?: boolean;

    /**
     * true bedeutet, dass ein Wert für den Parameter angegeben werden muss.
     */
    required?: boolean;

}

/**
 * Siehe {@link IPortalComponentInputMetadata}.
 */
export class PortalComponentInputMetadata implements IPortalComponentInputMetadata {
    description: string;
    type: string;
    multi: boolean;
    required: boolean;
}

/**
 * Properties, die an eine {@link PortalComponentOutput}-Annotation übergeben werden.
 */
export interface IPortalComponentOutputMetadata {

    /**
     * Die ausführliche Beschreibung des Input-Parameters, wie sie dem fachlichen Administrator angezeigt werden soll.
     */
    description: string;

}

/**
 * Siehe {@link IPortalComponentOutputMetadata}.
 */
export class PortalComponentOutputMetadata implements IPortalComponentOutputMetadata {
    description: string;
}
